/**
 * Diálogo de máquina ou equipamento do posto: modelo, peso, facilidade, compressão e controles. Grava na hora.
 */
import React, { useState } from 'react';
import { Grid, TextField } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicEquipment } from '../../../../../types/ergonomicWorkstation';
import { dialogFieldSx, parseWeightKg } from './dialogHelpers';
import { ItemDialog } from './ItemDialog';
import { useItemSubmit } from './useItemSubmit';

type EquipmentField =
  | 'model'
  | 'weightKg'
  | 'easeOfUse'
  | 'tissueCompression'
  | 'existingControls'
  | 'recommendedControls';

const SERVER_LABELS: ReadonlyArray<[string, EquipmentField]> = [
  ['Equipamento', 'model'],
  ['Peso', 'weightKg'],
  ['Facilidade de uso', 'easeOfUse'],
  ['Compressão de tecidos', 'tissueCompression'],
  ['Controles existentes', 'existingControls'],
  ['Controles recomendados', 'recommendedControls'],
];

interface EquipmentDialogProps {
  workstationId: number;
  item: ErgonomicEquipment | null;
  onSaved: (message: string) => void;
  onClose: () => void;
}

const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export const EquipmentDialog: React.FC<EquipmentDialogProps> = ({ workstationId, item, onSaved, onClose }) => {
  const [model, setModel] = useState(item?.model ?? '');
  const [weight, setWeight] = useState(item?.weightKg == null ? '' : String(item.weightKg));
  const [easeOfUse, setEaseOfUse] = useState(item?.easeOfUse ?? '');
  const [tissueCompression, setTissueCompression] = useState(item?.tissueCompression ?? '');
  const [existingControls, setExistingControls] = useState(item?.existingControls ?? '');
  const [recommendedControls, setRecommendedControls] = useState(item?.recommendedControls ?? '');
  const { submitting, error, errors, setErrors, run } = useItemSubmit<EquipmentField>(
    SERVER_LABELS,
    'Não foi possível guardar o equipamento. Tente de novo em alguns minutos.'
  );

  const submit = async () => {
    const parsedWeight = parseWeightKg(weight);
    const found: Partial<Record<EquipmentField, string>> = {};
    if (model.trim() === '') found.model = 'Diga qual máquina ou equipamento é usado.';
    if (parsedWeight.error) found.weightKg = parsedWeight.error;
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const saved = await run(async () => {
      const payload = {
        model: model.trim(),
        weightKg: parsedWeight.value,
        easeOfUse: blankToNull(easeOfUse),
        tissueCompression: blankToNull(tissueCompression),
        existingControls: blankToNull(existingControls),
        recommendedControls: blankToNull(recommendedControls),
      };
      if (item) await ergonomicWorkstationService.updateEquipment(workstationId, item.id, payload);
      else await ergonomicWorkstationService.createEquipment(workstationId, payload);
    });
    if (saved) onSaved(item ? 'Equipamento atualizado.' : 'Equipamento guardado.');
  };

  return (
    <ItemDialog
      title={item ? 'Editar equipamento' : 'Novo equipamento'}
      submitLabel="Guardar equipamento"
      submitting={submitting}
      error={error}
      onSubmit={submit}
      onClose={onClose}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            required
            label="Máquina ou equipamento (modelo)"
            value={model}
            onChange={(event) => setModel(event.target.value)}
            error={Boolean(errors.model)}
            helperText={errors.model || 'Ex.: Parafusadeira pneumática'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Peso (kg)"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            error={Boolean(errors.weightKg)}
            helperText={errors.weightKg || 'Ex.: 2,5'}
            disabled={submitting}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Facilidade de uso"
            value={easeOfUse}
            onChange={(event) => setEaseOfUse(event.target.value)}
            error={Boolean(errors.easeOfUse)}
            helperText={errors.easeOfUse || 'Ex.: Não, a mangueira de ar dificulta'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Compressão do tecido"
            value={tissueCompression}
            onChange={(event) => setTissueCompression(event.target.value)}
            error={Boolean(errors.tissueCompression)}
            helperText={errors.tissueCompression || 'Ex.: Na mão'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Controle existente"
            value={existingControls}
            onChange={(event) => setExistingControls(event.target.value)}
            error={Boolean(errors.existingControls)}
            helperText={errors.existingControls || 'O que já é feito hoje. Ex.: Uso esporádico'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Controle recomendado"
            value={recommendedControls}
            onChange={(event) => setRecommendedControls(event.target.value)}
            error={Boolean(errors.recommendedControls)}
            helperText={errors.recommendedControls || 'O que você recomenda. Ex.: Avaliar o nível de vibração'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
      </Grid>
    </ItemDialog>
  );
};
